<template>
  <Dialog v-model:open="isOpen">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Import Agent Roster</DialogTitle>
        <DialogDescription>
          Download our
          <a href="/agent_roster_template.csv" target="_blank" class="font-semibold text-indigo-600">
            agent roster template
          </a>
          CSV, fill in your agent roster, then upload it here.
        </DialogDescription>
      </DialogHeader>

      <FileInput accept=".csv" multiple="false" class="!mt-0" :model-value="file" @update:model-value="file = $event" />

      <DialogFooter>
        <DialogClose as-child>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <Button :disabled="!hasFile || isUploading" @click="startImport">
          {{ isUploading ? 'Importing...' : 'Start Import' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { hFetch } from '@/api/hFetch';
import FileInput from '../shared/FileInput.vue';

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

const file = ref<FileList | null>(null);
const isUploading = ref(false);

const hasFile = computed(() => !!file.value && file.value.length > 0);

watch(
  () => props.open,
  open => {
    if (open) {
      file.value = null;
      isUploading.value = false;
    }
  }
);

const startImport = async () => {
  if (!hasFile.value) return;

  isUploading.value = true;
  try {
    const upload = file.value![0];
    const formData = new FormData();
    formData.append('agent_roster_import_request[file]', upload, upload.name);

    const data = await hFetch<{ status: 'success' | 'error' }, FormData>({
      url: '/api/agent_roster_import_requests',
      method: 'POST',
      payload: formData,
    });

    if (data?.status === 'success') {
      isOpen.value = false;
      toast.success('Import started', {
        description:
          "Your agents are being added in the background. We'll send you an email once the import is complete.",
      });
    }
  } finally {
    isUploading.value = false;
  }
};
</script>
